import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import { useAuth } from '@/context/AuthContext';
import { BookOpen, Calendar, DollarSign, FileText, Award, Bell } from 'lucide-react';

export default function StudentPortal() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  const stats = [
    { icon: Calendar, label: 'Attendance', value: '92%', color: 'bg-green-100 text-green-700' },
    { icon: BookOpen, label: 'Homework', value: '5 Pending', color: 'bg-blue-100 text-blue-700' },
    { icon: Award, label: 'Grade', value: 'A+', color: 'bg-purple-100 text-purple-700' },
    { icon: DollarSign, label: 'Fees Due', value: '₹5,000', color: 'bg-amber-100 text-amber-700' }
  ];

  const homework = [
    { subject: 'Mathematics', title: 'Chapter 5 Exercises', dueDate: 'Mar 10, 2026', status: 'pending' },
    { subject: 'English', title: 'Essay Writing', dueDate: 'Mar 12, 2026', status: 'submitted' },
    { subject: 'Science', title: 'Lab Report', dueDate: 'Mar 15, 2026', status: 'pending' }
  ];

  const results = [
    { exam: 'Mid-Term 2026', subject: 'Mathematics', marks: 92, total: 100, grade: 'A+' },
    { exam: 'Mid-Term 2026', subject: 'English', marks: 88, total: 100, grade: 'A' },
    { exam: 'Mid-Term 2026', subject: 'Science', marks: 95, total: 100, grade: 'A+' }
  ];

  return (
    <Layout>
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-2">Welcome, {user?.displayName || 'Student'}!</h1>
          <p className="text-blue-100">Student Portal Dashboard</p>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {stats.map((stat, i) => (
              <GlassCard key={i} className="text-center">
                <div className={`w-16 h-16 ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <stat.icon className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold text-blue-900 mb-1">{stat.value}</h3>
                <p className="text-slate-600 font-medium">{stat.label}</p>
              </GlassCard>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Homework Assignments</h2>
              <div className="space-y-4">
                {homework.map((hw, i) => (
                  <GlassCard key={i} className="flex items-center justify-between">
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-amber-600 uppercase">{hw.subject}</span>
                      <h3 className="text-lg font-bold text-blue-900 mb-1">{hw.title}</h3>
                      <p className="text-sm text-slate-600">Due: {hw.dueDate}</p>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      hw.status === 'submitted' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {hw.status === 'submitted' ? 'Submitted' : 'Pending'}
                    </span>
                  </GlassCard>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-blue-900 mb-6 mt-12">Exam Results</h2>
              <GlassCard>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-blue-900">Subject</th>
                        <th className="text-left py-3 px-4 font-semibold text-blue-900">Marks</th>
                        <th className="text-left py-3 px-4 font-semibold text-blue-900">Grade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((result, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="py-3 px-4 text-slate-700">{result.subject}</td>
                          <td className="py-3 px-4 text-slate-700">{result.marks}/{result.total}</td>
                          <td className="py-3 px-4">
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                              {result.grade}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Quick Actions</h2>
              <div className="space-y-4">
                <GlassCard className="hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-6 h-6 text-blue-900" />
                    <span className="font-semibold text-blue-900">Download Report Card</span>
                  </div>
                </GlassCard>
                <GlassCard className="hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <DollarSign className="w-6 h-6 text-blue-900" />
                    <span className="font-semibold text-blue-900">Pay Fees Online</span>
                  </div>
                </GlassCard>
                <GlassCard className="hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-6 h-6 text-blue-900" />
                    <span className="font-semibold text-blue-900">View Timetable</span>
                  </div>
                </GlassCard>
              </div>

              <h2 className="text-2xl font-bold text-blue-900 mb-6 mt-8">Notices</h2>
              <GlassCard className="bg-amber-50">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">PTM on March 20</p>
                      <p className="text-xs text-slate-600">2 days ago</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Sports Day Registration</p>
                      <p className="text-xs text-slate-600">5 days ago</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
